/**
 * Anchor Parser for Interactive Components
 * 
 * Parses anchor syntax like [heatmap(sector="S&P")] from LLM responses
 * and converts them into structured component data.
 */

export type AnchorParams = Record<string, string>;

export type ParsedAnchor = {
  type: 'text' | 'component';
  content: string;
  componentName?: string;
  params?: AnchorParams;
};

/**
 * Regex pattern for matching anchor syntax:
 * - Component name: starts with lowercase letter or underscore, followed by lowercase letters, digits, underscores, or hyphens
 * - Parameters: key="value" pairs, comma-separated
 * - Handles escaped quotes inside values: \\"
 */
const ANCHOR_REGEX = /\[([a-z_][a-z0-9_-]*)\((.*?)\)\]/g;

/**
 * Parse parameter string like: sector="S&P", filter="tech"
 * Returns object: { sector: "S&P", filter: "tech" }
 */
function parseParams(paramStr: string): AnchorParams {
  const params: AnchorParams = {};
  
  if (!paramStr.trim()) {
    return params;
  }

  // Match key="value" pairs, handling escaped quotes
  const paramRegex = /([a-z0-9_]+)\s*=\s*"((?:[^"\\]|\\.)*)"/g;
  let match;

  while ((match = paramRegex.exec(paramStr)) !== null) {
    const key = match[1];
    const value = match[2].replace(/\\"/g, '"'); // Unescape quotes
    params[key] = value;
  }

  return params;
}

/**
 * Parse text containing anchors into an array of text and component segments.
 * 
 * Example:
 * Input: "Here's the data: [heatmap(sector="tech")] and more text"
 * Output: [
 *   { type: 'text', content: "Here's the data: " },
 *   { type: 'component', content: '[heatmap(sector="tech")]', componentName: 'heatmap', params: { sector: 'tech' } },
 *   { type: 'text', content: " and more text" }
 * ]
 */
export function parseAnchors(text: string): ParsedAnchor[] {
  const segments: ParsedAnchor[] = [];
  let lastIndex = 0;

  // Reset regex state
  ANCHOR_REGEX.lastIndex = 0;

  let match;
  while ((match = ANCHOR_REGEX.exec(text)) !== null) {
    const fullMatch = match[0];
    const componentName = match[1];
    const paramStr = match[2];
    const matchIndex = match.index;

    // Add text before this anchor
    if (matchIndex > lastIndex) {
      const textContent = text.slice(lastIndex, matchIndex);
      segments.push({
        type: 'text',
        content: textContent,
      });
    }

    // Add component anchor
    segments.push({
      type: 'component',
      content: fullMatch,
      componentName,
      params: parseParams(paramStr),
    });

    lastIndex = matchIndex + fullMatch.length;
  }

  // Add remaining text after last anchor
  if (lastIndex < text.length) {
    segments.push({
      type: 'text',
      content: text.slice(lastIndex),
    });
  }

  // If no anchors found, return the whole text as a single segment
  if (segments.length === 0) {
    segments.push({
      type: 'text',
      content: text,
    });
  }

  return segments;
}

/**
 * Check if text contains any anchors
 */
export function hasAnchors(text: string): boolean {
  ANCHOR_REGEX.lastIndex = 0;
  return ANCHOR_REGEX.test(text);
}

/**
 * Extract all anchors from text (without text segments)
 */
export function extractAnchors(text: string): ParsedAnchor[] {
  return parseAnchors(text).filter(segment => segment.type === 'component');
}
